import { Density } from '@philips-emr/tws-constants';
import { AwesomeEvent, ComponentAPI, Value } from '@philips-emr/tws-core';
import Changeable, { ChangeEventListener } from '@philips-emr/tws-core/lib/component/types/changeable';
import Editable, { EditEventListener } from '@philips-emr/tws-core/lib/component/types/editable';
import Submitable, { SubmitEventListener } from '@philips-emr/tws-core/lib/component/types/submitable';
import Validatable, { ValidateEventListener } from '@philips-emr/tws-core/lib/component/types/validatable';
import { Validation } from '@philips-emr/tws-core/lib/services/model-service';
import { createAbort, isEnumValid, noop } from '@philips-emr/tws-utils';

class FormAPI extends ComponentAPI implements Submitable, Editable, Changeable<Record<string, Value>>, Validatable {

  /** @event */
  @AwesomeEvent
  onEdit = new EditEventListener();

  /** @event */
  @AwesomeEvent
  onBeforeSubmit = new SubmitEventListener();

  /** @event */
  @AwesomeEvent
  onSubmit = new SubmitEventListener();

  /** @event */
  @AwesomeEvent
  onBeforeCancel = new SubmitEventListener();

  /** @event */
  @AwesomeEvent
  onCancel = new SubmitEventListener();

  /** @event */
  @AwesomeEvent
  onChange = new ChangeEventListener<Record<string, Value>>();

  /** @event */
  @AwesomeEvent
  onValidate = new ValidateEventListener();

  getTitle(): string {
    return this.state.title;
  }

  setTitle(title: string): void {
    this.setState({ title });
  }

  getData(): Record<string, Value> {
    return this.state.data;
  }

  setData(data: Record<string, Value>): void {
    this.setState({ data });
  }

  getRecord(): Record<string, Value> {
    const { record, recordChanges } = this.state;
    return { ...record, ...recordChanges };
  }

  setRecord(record: Record<string, Value>): void {
    this.setState({ record, recordChanges: {} });
  }

  isDisabled(): boolean {
    return this.state.disabled;
  }

  setDisabled(disabled: boolean): void {
    this.setState({ disabled });
  }

  getDensity(): Density {
    return this.state.density;
  }

  setDensity(density: Density): void {
    if (!isEnumValid(Density, density)) return;

    this.setState({ density });
  }

  isEditing(): boolean {
    return this.state.editing;
  }

  setEditing(editing: boolean): void {
    this.setState({ editing });
  }

  submit(): void {
    if (!this.validate()) return;

    const record = this.getRecord();

    const abort = createAbort();

    this.onBeforeSubmit.fire({ record, abort });

    abort.promise.then(aborted => {
      if (aborted) return;

      this.onSubmit.fire({ record, abort: noop });
      this.setEditing(false);
    });
  }

  cancel(): void {
    const record = this.getRecord();

    const abort = createAbort();

    this.onBeforeCancel.fire({ record, abort });

    abort.promise.then(aborted => {
      if (aborted) return;

      this.onCancel.fire({ record, abort: noop });
      this.setRecord(this.state.data);
      this.setEditing(false);
    });
  }

  getValue(): Record<string, Value> {
    return this.getRecord();
  }

  setValue(data: Record<string, Value>): void {
    this.setRecord(data);
  }

  isLocked(): boolean {
    return this.state.locked;
  }

  setLocked(locked: boolean): void {
    this.setState({ locked });
  }

  validate(): boolean {
    const validations = this.getValidations();
    const valid = this.isValid();

    this.setState({ forceValidate: !valid });

    this.onValidate.fire({ validations, valid });

    return valid;
  }

  isValid(): boolean {
    return this.state.valid;
  }

  isLoading(): boolean {
    return this.state.loading;
  }

  setLoading(loading: boolean): void {
    this.setState({ loading });
  }

  private getValidations(): Validation[] {
    const { validationRecord } = this.state;
    return Object.values(validationRecord);
  }
}

export default FormAPI;
